import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, deleteField, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, db } from '../firebase';
import PageShell from './PageShell';
import LoadingState from './LoadingState';
import { ArrowRightIcon, CasesIcon, CopyIcon, DeleteIcon, DocumentsIcon, InfoIcon, PlusIcon } from './AppIcons';
import {
  createDraftingSession,
  estimateDraftingUsage,
  exportDraftingDocx,
  extractDraftingSources,
  generateDraftingOutput,
  getStorageUrl,
  publishDraftingOutput,
  registerDraftingSource,
  uploadDraftingFile,
} from '../utils/drafting';
import { calculateAgeFromDateOfBirth, createClientProfile, extractAadhaarDetails } from '../utils/clientProfiles';
import {
  genderOptions,
  isClientDraftReady,
  relationLabelOptions,
} from '../utils/draftingProfiles';
import useAiAccessSummary from '../utils/useAiAccessSummary';
import { canUseAiNow, getAiCreditHeadline } from '../utils/billing';

const emptyDraftForm = {
  clientId: '',
  caseId: '',
  instructions: '',
};

const emptyClientForm = {
  name: '',
  phone: '',
  email: '',
  preferredLanguage: 'en',
  relationLabel: 'S/o',
  relationName: '',
  age: '',
  dateOfBirth: '',
  gender: 'Male',
  address: '',
  aadhaarName: '',
  aadhaarNumber: '',
};

const emptyAadhaarStatus = { loading: false, success: false, warnings: [], rawText: '', error: '' };

const workflowLabels = {
  draft: 'Ready',
  extracting: 'Reading files',
  ready_for_review: 'Review facts',
  generating: 'Generating',
  completed: 'Ready to review',
  failed: 'Needs attention',
};

const escapeRegExp = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const applyValidatedFactsToDraft = (draftText = '', fields = [], t) => {
  let nextText = draftText || '';

  fields.forEach((field) => {
    const trimmedValue = (field.value || '').trim();
    if (!trimmedValue || !field.placeholder) {
      return;
    }

    const placeholderPattern = new RegExp(`\\[${escapeRegExp(field.placeholder)}\\]`, 'gi');
    nextText = nextText.replace(placeholderPattern, trimmedValue);
  });

  const unresolved = fields
    .filter((field) => !(field.value || '').trim())
    .map((field) => field.label);

  const footerLines = [
    '',
    '---',
    '',
    t('draftingReviewFooterTitle'),
    unresolved.length
      ? `${t('draftingReviewFooterPending')} ${unresolved.join(', ')}.`
      : t('draftingReviewFooterClean'),
  ];

  const reviewMarkerPattern = /\n---\n\nAdvocate review note:[\s\S]*$/i;
  if (reviewMarkerPattern.test(nextText)) {
    nextText = nextText.replace(reviewMarkerPattern, footerLines.join('\n'));
  } else {
    nextText = `${nextText.trim()}\n${footerLines.join('\n')}`;
  }

  return nextText.trim();
};

const createProgressState = (overrides = {}) => ({
  active: false,
  stageKey: 'idle',
  stageLabel: '',
  detail: '',
  currentStep: 0,
  totalSteps: 4,
  uploadedFiles: 0,
  estimatedTokens: 0,
  ...overrides,
});

const findClientIdForCaseRecord = (caseRecord, clientList = []) => {
  if (!caseRecord) return '';
  if (caseRecord.client_id) return caseRecord.client_id;
  const matchedClient = clientList.find((client) =>
    client.name === caseRecord.client_name ||
    (client.email && client.email === caseRecord.client_email) ||
    (client.phone && client.phone === caseRecord.client_phone)
  );
  return matchedClient?.id || '';
};

const DraftingAssistant = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionParam = searchParams.get('sessionId') || '';
  const caseParam = searchParams.get('caseId') || '';
  const view = searchParams.get('view') || '';
  const advocateId = auth.currentUser?.uid || '';

  const [draftForm, setDraftForm] = useState(emptyDraftForm);
  const [clientForm, setClientForm] = useState(emptyClientForm);
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [draftSources, setDraftSources] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [output, setOutput] = useState(null);
  const [composeStep, setComposeStep] = useState(0);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientIntakeMode, setNewClientIntakeMode] = useState('aadhaar');
  const [validationFields, setValidationFields] = useState([]);
  const [progress, setProgress] = useState(createProgressState());
  const [aadhaarStatus, setAadhaarStatus] = useState(emptyAadhaarStatus);
  const [usageEstimate, setUsageEstimate] = useState(null);
  const [usageSummaryNote, setUsageSummaryNote] = useState('');
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const { summary: aiSummary, refresh: refreshAiSummary } = useAiAccessSummary();

  const currentSession = useMemo(
    () => sessions.find((session) => session.id === sessionParam) || null,
    [sessionParam, sessions]
  );

  const activeClient = useMemo(
    () => clients.find((client) => client.id === draftForm.clientId) || null,
    [clients, draftForm.clientId]
  );

  const activeCase = useMemo(
    () => cases.find((caseRecord) => caseRecord.id === draftForm.caseId) || null,
    [cases, draftForm.caseId]
  );

  const canPublishDraft = Boolean(currentSession?.id && output?.id);
  const failedSources = useMemo(
    () => draftSources.filter((source) => source.status === 'failed'),
    [draftSources]
  );
  const promptPreviewText = useMemo(() => (
    draftSources
      .map((source) => (source.reviewed_text || source.raw_extracted_text || '').trim())
      .filter(Boolean)
      .join('\n\n---\n\n')
      .slice(0, 4000)
  ), [draftSources]);
  const shouldShowNewClientFields = newClientIntakeMode === 'manual' || aadhaarStatus.success || Boolean(aadhaarStatus.error) || Boolean(aadhaarStatus.rawText);
  const draftingOverlayLabel = aadhaarStatus.loading ? t('aadhaarProcessingTitle') : (progress.stageLabel || t('draftingProcessingTitle'));
  const showProcessingOverlay = aadhaarStatus.loading || working;
  const aiLocked = !canUseAiNow(aiSummary || {});

  const availableCases = useMemo(() => {
    if (!draftForm.clientId) return cases;
    return cases.filter((caseRecord) => {
      if (caseRecord.client_id) return caseRecord.client_id === draftForm.clientId;
      const matchedClient = clients.find((client) => client.id === draftForm.clientId);
      return (
        caseRecord.client_name === matchedClient?.name ||
        (matchedClient?.phone && caseRecord.client_phone === matchedClient.phone) ||
        (matchedClient?.email && caseRecord.client_email === matchedClient.email)
      );
    });
  }, [cases, clients, draftForm.clientId]);

  const isActiveClientDraftReady = !activeClient || isClientDraftReady(activeClient);

  const moveToComposeStep = (nextStep) => {
    setComposeStep(Math.min(3, Math.max(1, nextStep)));
  };

  const startComposeFlow = () => {
    if (aiLocked) {
      requestPremiumFeature();
      return;
    }
    setComposeStep(1);
    setStatusMessage('');
  };

  const fetchArtifacts = useCallback(async (sessionId, ownerId = auth.currentUser?.uid) => {
    if (!sessionId || !ownerId) return { nextSources: [], nextOutput: null };
    const [sourcesSnapshot, outputSnapshot] = await Promise.all([
      getDocs(query(collection(db, 'drafting_sources'), where('session_id', '==', sessionId), where('advocate_id', '==', ownerId))),
      getDocs(query(collection(db, 'drafting_outputs'), where('session_id', '==', sessionId), where('advocate_id', '==', ownerId))),
    ]);
    return {
      nextSources: sourcesSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })),
      nextOutput: outputSnapshot.docs.map((item) => ({ id: item.id, ...item.data() }))[0] || null,
    };
  }, []);

  const loadWorkspace = useCallback(async (targetSessionId = sessionParam) => {
    if (!advocateId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [casesSnapshot, clientsSnapshot, sessionsSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'cases'), where('advocate_id', '==', advocateId))),
        getDocs(query(collection(db, 'clients'), where('advocate_id', '==', advocateId))),
        getDocs(query(collection(db, 'drafting_sessions'), where('advocate_id', '==', advocateId))),
      ]);

      const nextCases = casesSnapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      const nextClients = clientsSnapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      const nextSessions = sessionsSnapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .sort((left, right) => (right.updated_at?.seconds || 0) - (left.updated_at?.seconds || 0));

      setCases(nextCases);
      setClients(nextClients);
      setSessions(nextSessions);

      const selectedSession = targetSessionId ? nextSessions.find((session) => session.id === targetSessionId) || null : null;
      if (selectedSession) {
        setComposeStep(1);
        setDraftForm({
          clientId: selectedSession.client_id || findClientIdForCaseRecord(nextCases.find((caseRecord) => caseRecord.id === selectedSession.case_id) || null, nextClients),
          caseId: selectedSession.case_id || '',
          instructions: selectedSession.instructions || '',
        });
        const { nextSources, nextOutput } = await fetchArtifacts(selectedSession.id, advocateId);
        setDraftSources(nextSources);
        setOutput(nextOutput);
        setValidationFields(nextOutput?.fact_validation_fields || []);
      } else {
        setComposeStep(0);
        const selectedCase = nextCases.find((caseRecord) => caseRecord.id === caseParam) || null;
        setDraftForm({
          clientId: findClientIdForCaseRecord(selectedCase, nextClients),
          caseId: selectedCase?.id || '',
          instructions: selectedCase ? `${t('draftingCasePrefillInstructions')} ${selectedCase.case_number}.` : '',
        });
        setDraftSources([]);
        setOutput(null);
        setValidationFields([]);
      }

      setStatusMessage('');
    } catch (error) {
      setStatusMessage(error.message || 'Unable to load the drafting workflow right now.');
    } finally {
      setLoading(false);
    }
  }, [advocateId, caseParam, fetchArtifacts, sessionParam, t]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    if (view !== 'prepare' || !currentSession?.id) {
      setUsageEstimate(null);
      return;
    }

    let active = true;

    estimateDraftingUsage({ feature: 'draft_generation', sessionId: currentSession.id })
      .then((result) => {
        if (active) {
          setUsageEstimate(result);
        }
      })
      .catch(() => {
        if (active) {
          setUsageEstimate(null);
        }
      });

    return () => {
      active = false;
    };
  }, [currentSession?.id, view]);

  const buildSessionPatch = useCallback(() => {
    const client = clients.find((item) => item.id === draftForm.clientId) || null;
    const caseRecord = cases.find((item) => item.id === draftForm.caseId) || null;
    return {
      client_id: client?.id || '',
      client_name: client?.name || caseRecord?.client_name || '',
      case_id: caseRecord?.id || '',
      case_number: caseRecord?.case_number || '',
      instructions: draftForm.instructions,
      output_language: client?.preferredLanguage || caseRecord?.client_language || 'en',
      client_profile_snapshot: client ? {
        clientId: client.id,
        name: client.name || '',
        phone: client.phone || '',
        email: client.email || '',
        preferredLanguage: client.preferredLanguage || 'en',
        relationLabel: client.relationLabel || '',
        relationName: client.relationName || '',
        age: client.age || '',
        dateOfBirth: client.dateOfBirth || '',
        gender: client.gender || '',
        address: client.address || '',
        aadhaarName: client.aadhaarName || '',
        aadhaarNumber: client.aadhaarNumber || '',
      } : {},
      case_snapshot: caseRecord ? {
        caseId: caseRecord.id,
        caseNumber: caseRecord.case_number || '',
        clientName: caseRecord.client_name || '',
        court: caseRecord.court || '',
        place: caseRecord.place || '',
        policeStation: caseRecord.police_station || '',
        status: caseRecord.status || '',
        summary: caseRecord.summary || '',
        nextStep: caseRecord.next_step || '',
      } : {},
      updated_at: new Date(),
    };
  }, [cases, clients, draftForm.caseId, draftForm.clientId, draftForm.instructions]);

  const ensureSession = useCallback(async () => {
    if (sessionParam) {
      await updateDoc(doc(db, 'drafting_sessions', sessionParam), buildSessionPatch());
      return sessionParam;
    }

    const created = await createDraftingSession({
      clientId: draftForm.clientId,
      caseId: draftForm.caseId,
      draftType: 'auto',
      customDraftType: '',
      instructions: draftForm.instructions,
    });

    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('sessionId', created.sessionId);
      if (draftForm.caseId) next.set('caseId', draftForm.caseId);
      return next;
    }, { replace: true });

    return created.sessionId;
  }, [buildSessionPatch, draftForm.caseId, draftForm.clientId, draftForm.instructions, sessionParam, setSearchParams]);

  const estimatePromptTokens = useCallback(() => {
    const instructionTokens = Math.ceil((draftForm.instructions || '').trim().length / 4);
    const contextTokens = activeCase ? 180 : 90;
    const fileTokens = selectedFiles.length * 320;
    return instructionTokens + contextTokens + fileTokens;
  }, [activeCase, draftForm.instructions, selectedFiles.length]);

  const updateProgressStage = useCallback((stageKey, currentStep, detail, overrides = {}) => {
    const stageMap = {
      session: t('draftingProgressSession'),
      upload: t('draftingProgressUpload'),
      extract: t('draftingProgressExtract'),
      generate: t('draftingProgressGenerate'),
      validate: t('draftingProgressValidate'),
    };

    setProgress((current) => createProgressState({
      ...current,
      active: true,
      stageKey,
      stageLabel: stageMap[stageKey] || '',
      detail,
      currentStep,
      totalSteps: 4,
      uploadedFiles: selectedFiles.length,
      estimatedTokens: estimatePromptTokens(),
      ...overrides,
    }));
  }, [estimatePromptTokens, selectedFiles.length, t]);

  const applyAadhaarFields = useCallback((extracted = {}) => {
    setClientForm((current) => ({
      ...current,
      name: current.name || extracted.name || extracted.aadhaarName || '',
      aadhaarName: extracted.aadhaarName || current.aadhaarName || '',
      aadhaarNumber: extracted.aadhaarNumber || current.aadhaarNumber || '',
      dateOfBirth: extracted.dateOfBirth || current.dateOfBirth || '',
      age: extracted.age || current.age || calculateAgeFromDateOfBirth(extracted.dateOfBirth || current.dateOfBirth || ''),
      gender: extracted.gender || current.gender || '',
      address: extracted.address || current.address || '',
    }));
  }, []);

  const handleAadhaarUpload = useCallback(async (file) => {
    setAadhaarFile(file || null);
    if (!file || !advocateId) {
      setAadhaarStatus(emptyAadhaarStatus);
      return;
    }

    setAadhaarStatus({ loading: true, success: false, warnings: [], rawText: '', error: '' });
    try {
      const result = await extractAadhaarDetails({ advocateId, file });
      applyAadhaarFields(result.extracted);
      setAadhaarStatus({
        loading: false,
        success: result.success,
        warnings: result.warnings || [],
        rawText: result.extracted?.rawText || '',
        error: '',
      });
    } catch (error) {
      setAadhaarStatus({
        loading: false,
        success: false,
        warnings: [],
        rawText: '',
        error: error.message || t('aadhaarReadFailed'),
      });
    }
  }, [advocateId, applyAadhaarFields, t]);

  const handleSourceReviewChange = (sourceId, value) => {
    setDraftSources((current) => current.map((source) => (
      source.id === sourceId ? { ...source, reviewed_text: value } : source
    )));
  };

  const startDrafting = async () => {
    if (!draftForm.clientId) {
      setStatusMessage(t('selectClientBeforeDrafting'));
      return;
    }
    if (!draftForm.instructions.trim()) {
      setStatusMessage(t('draftingInstructionsRequired'));
      return;
    }
    if (aiLocked) {
      requestPremiumFeature();
      return;
    }

    setWorking(true);
    setStatusMessage(t('draftingPreparing'));
    setUsageSummaryNote('');
    updateProgressStage('session', 1, t('draftingProgressSessionDetail'));
    try {
      const sessionId = await ensureSession();
      const sourceIds = [];

      if (selectedFiles.length) {
        updateProgressStage('upload', 2, t('draftingProgressUploadDetail', { count: selectedFiles.length }));
      }

      for (const file of selectedFiles) {
        const upload = await uploadDraftingFile({ advocateId, sessionId, file });
        const registered = await registerDraftingSource({
          sessionId,
          sourceType: 'uploaded_file',
          name: file.name,
          label: file.name,
          mimeType: file.type || 'application/octet-stream',
          storagePath: upload.storagePath,
          url: upload.url,
        });
        if (registered?.sourceId) sourceIds.push(registered.sourceId);
      }

      if (sourceIds.length) {
        updateProgressStage('extract', 3, t('draftingProgressExtractDetail', { count: sourceIds.length }));
        const extraction = await extractDraftingSources({ sessionId, sourceIds });
        if (extraction?.creditsUsed) {
          setUsageSummaryNote(t('draftingCreditsAfterRun', {
            credits: extraction.creditsUsed,
            remaining: extraction.remainingCredits?.totalCredits || 0,
          }));
          await refreshAiSummary();
        }
      }
      const { nextSources, nextOutput } = await fetchArtifacts(sessionId, advocateId);
      setDraftSources(nextSources);
      setOutput(nextOutput);
      setValidationFields(nextOutput?.fact_validation_fields || []);
      setSelectedFiles([]);
      await loadWorkspace(sessionId);
      setProgress(createProgressState());
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.set('sessionId', sessionId);
        next.set('view', 'prepare');
        return next;
      }, { replace: true });
      setStatusMessage(t('draftingPromptReviewReady'));
    } catch (error) {
      if (/trial|subscribe|top up|credits/i.test(error.message || '')) {
        requestPremiumFeature();
      }
      setStatusMessage(error.message);
      setProgress((current) => createProgressState({
        ...current,
        active: true,
        stageLabel: t('draftingProgressFailed'),
        detail: error.message,
      }));
    } finally {
      setWorking(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    setSelectedFiles((current) => [...current, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop, disabled: working });

  const requestPremiumFeature = useCallback(() => {
    setShowPremiumModal(true);
  }, []);

  const handleCreateClient = async (event) => {
    event.preventDefault();
    if (!advocateId) return;
    setWorking(true);
    try {
      const clientId = await createClientProfile({
        advocateId,
        data: {
          advocate_id: advocateId,
          ...clientForm,
          age: clientForm.age || calculateAgeFromDateOfBirth(clientForm.dateOfBirth),
          draftReady: true,
        },
        aadhaarFile,
      });
      setClientForm(emptyClientForm);
      setAadhaarFile(null);
      setAadhaarStatus(emptyAadhaarStatus);
      setNewClientIntakeMode('aadhaar');
      setShowNewClientForm(false);
      await loadWorkspace();
      setDraftForm((current) => ({ ...current, clientId }));
      setComposeStep(2);
      setStatusMessage(t('clientAddedForDrafting'));
    } catch (error) {
      setStatusMessage(error.message);
    } finally {
      setWorking(false);
    }
  };

  const openSession = async (sessionId) => {
    if (!sessionId) return;

    setWorking(true);
    try {
      const session = sessions.find((item) => item.id === sessionId) || null;
      const { nextSources, nextOutput } = await fetchArtifacts(sessionId, advocateId);
      setDraftSources(nextSources);
      setOutput(nextOutput);
      setValidationFields(nextOutput?.fact_validation_fields || []);

      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.set('sessionId', sessionId);
        if (session?.case_id) {
          next.set('caseId', session.case_id);
        }

        if (nextOutput) {
          setComposeStep(0);
          next.set('view', nextOutput.fact_validation_required ? 'validate' : 'review');
        } else if (nextSources.length) {
          setComposeStep(0);
          next.set('view', 'prepare');
        } else {
          setComposeStep(1);
          next.delete('view');
        }

        return next;
      }, { replace: true });
    } catch (error) {
      setStatusMessage(error.message || 'Unable to resume this drafting workflow.');
    } finally {
      setWorking(false);
    }
  };

  const clearSessionParams = () => {
    setComposeStep(0);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('sessionId');
      next.delete('view');
      return next;
    }, { replace: true });
  };

  const handleDiscardSession = async (sessionId) => {
    if (!sessionId) return;
    setWorking(true);
    try {
      const [sourceSnapshot, outputSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'drafting_sources'), where('session_id', '==', sessionId), where('advocate_id', '==', advocateId))),
        getDocs(query(collection(db, 'drafting_outputs'), where('session_id', '==', sessionId), where('advocate_id', '==', advocateId))),
      ]);
      await Promise.all([
        ...sourceSnapshot.docs.map((item) => deleteDoc(item.ref)),
        ...outputSnapshot.docs.map((item) => deleteDoc(item.ref)),
        deleteDoc(doc(db, 'drafting_sessions', sessionId)),
      ]);
      if (sessionId === sessionParam) {
        clearSessionParams();
        setDraftForm(emptyDraftForm);
        setOutput(null);
        setDraftSources([]);
        setValidationFields([]);
        setSelectedFiles([]);
      }
      await loadWorkspace();
      setStatusMessage('Drafting workflow discarded.');
    } catch (error) {
      setStatusMessage(error.message);
    } finally {
      setWorking(false);
    }
  };

  const handleValidationChange = (fieldKey, value) => {
    setValidationFields((current) => current.map((field) => (field.key === fieldKey ? { ...field, value } : field)));
  };

  const handleFactValidation = async () => {
    if (!currentSession || !output?.id) return;
    setWorking(true);
    setStatusMessage(t('draftingValidationSaving'));
    try {
      const validatedFacts = validationFields.reduce((accumulator, field) => {
        accumulator[field.key] = field.value || '';
        return accumulator;
      }, {});

      const clientPatch = {};
      const advocatePatch = {};
      const casePatch = {};

      validationFields.forEach((field) => {
        if (!field.sourceField) return;
        if (field.target === 'client') clientPatch[field.sourceField] = field.value || '';
        if (field.target === 'advocate') advocatePatch[field.sourceField] = field.value || '';
        if (field.target === 'case') casePatch[field.sourceField] = field.value || '';
      });

      await updateDoc(doc(db, 'drafting_sessions', currentSession.id), {
        validated_facts: validatedFacts,
        ...buildSessionPatch(),
        updated_at: new Date(),
      });

      const updates = [];
      if (currentSession.client_id && Object.keys(clientPatch).length) {
        updates.push(updateDoc(doc(db, 'clients', currentSession.client_id), clientPatch));
      }
      if (Object.keys(advocatePatch).length) {
        updates.push(updateDoc(doc(db, 'users', advocateId), advocatePatch));
      }
      if (currentSession.case_id && Object.keys(casePatch).length) {
        updates.push(updateDoc(doc(db, 'cases', currentSession.case_id), casePatch));
      }
      await Promise.all(updates);

      const patchedDraftText = applyValidatedFactsToDraft(
        output.edited_text || output.generated_text || '',
        validationFields,
        t
      );

      await Promise.all([
        updateDoc(doc(db, 'drafting_outputs', output.id), {
          edited_text: patchedDraftText,
          fact_validation_required: false,
          fact_validation_completed_at: new Date(),
          updated_at: new Date(),
        }),
        updateDoc(doc(db, 'drafting_sessions', currentSession.id), {
          status: 'completed',
          updated_at: new Date(),
        }),
      ]);

      setOutput((current) => ({
        ...current,
        edited_text: patchedDraftText,
        fact_validation_required: false,
        fact_validation_completed_at: new Date(),
      }));
      setProgress(createProgressState());
      setStatusMessage(t('draftingValidationComplete'));
      await loadWorkspace(currentSession.id);
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.set('view', 'review');
        return next;
      }, { replace: true });
    } catch (error) {
      setStatusMessage(error.message);
    } finally {
      setWorking(false);
    }
  };

  const handleGenerateFromReview = async () => {
    if (!currentSession?.id) return;
    if (aiLocked) {
      requestPremiumFeature();
      return;
    }
    setWorking(true);
    setStatusMessage(t('draftingPreparingPrompt'));
    setUsageSummaryNote('');
    updateProgressStage('generate', 4, t('draftingProgressGenerateDetail'));

    try {
      await Promise.all(draftSources.map((source) => {
        const reviewedText = (source.reviewed_text || '').trim();
        if (reviewedText) {
          return updateDoc(doc(db, 'drafting_sources', source.id), {
            reviewed_text: reviewedText,
            raw_extracted_text: source.raw_extracted_text || reviewedText,
            extraction_method: source.extraction_method || 'manual_text',
            status: 'ready_for_review',
            error_message: deleteField(),
            updated_at: new Date(),
          });
        }

        return Promise.resolve();
      }));

      const generation = await generateDraftingOutput({ sessionId: currentSession.id });
      const { nextSources, nextOutput } = await fetchArtifacts(currentSession.id, advocateId);
      setDraftSources(nextSources);
      setOutput(nextOutput);
      setValidationFields(nextOutput?.fact_validation_fields || []);
      if (generation?.creditsUsed) {
        setUsageSummaryNote(t('draftingCreditsAfterRun', {
          credits: generation.creditsUsed,
          remaining: generation.remainingCredits?.totalCredits || 0,
        }));
        await refreshAiSummary();
      }
      await loadWorkspace(currentSession.id);
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.set('view', generation.requiresValidation ? 'validate' : 'review');
        return next;
      }, { replace: true });
      setProgress(createProgressState());
    } catch (error) {
      if (/trial|subscribe|top up|credits/i.test(error.message || '')) {
        requestPremiumFeature();
      }
      setStatusMessage(error.message);
      setProgress((current) => createProgressState({
        ...current,
        active: true,
        stageLabel: t('draftingProgressFailed'),
        detail: error.message,
      }));
    } finally {
      setWorking(false);
    }
  };

  const handleSaveDraftEdits = async () => {
    if (!output?.id) return;
    setWorking(true);
    try {
      await updateDoc(doc(db, 'drafting_outputs', output.id), { edited_text: output.edited_text, updated_at: new Date() });
      setStatusMessage(t('draftingDraftSaved'));
    } catch (error) {
      setStatusMessage(error.message);
    } finally {
      setWorking(false);
    }
  };

  const handleCopy = async () => {
    if (!output?.edited_text) return;
    await navigator.clipboard.writeText(output.edited_text);
    setStatusMessage(t('draftingCopied'));
  };

  const handleExport = async () => {
    if (!output?.id || !currentSession) return;
    setWorking(true);
    try {
      const exportResult = await exportDraftingDocx({ sessionId: currentSession.id, outputId: output.id });
      const exportUrl = await getStorageUrl(exportResult.exportPath);
      window.open(exportUrl, '_blank', 'noopener,noreferrer');
      setStatusMessage(t('draftingExportReady'));
    } catch (error) {
      setStatusMessage(error.message);
    } finally {
      setWorking(false);
    }
  };

  const handlePublish = async () => {
    if (!output?.id || !currentSession) return;
    setWorking(true);
    try {
      await publishDraftingOutput({ sessionId: currentSession.id, outputId: output.id });
      setStatusMessage(t('draftingPublishComplete'));
    } catch (error) {
      setStatusMessage(error.message);
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return <PageShell title={t('aiDraftingAssistant')} subtitle={t('aiDraftingSubtitle')} showBack><LoadingState label={t('loadingDraftingWorkspace')} /></PageShell>;
  }

  if (view === 'prepare' && currentSession) {
    return (
      <PageShell
        title={t('reviewAiContext')}
        subtitle={currentSession.case_number || activeClient?.name || t('aiDraftingSubtitle')}
        showBack
        backTo="/drafting"
        backReplace
      >
        <section className="panel workflow-card">
          <div className="section-heading">
            <div>
              <p className="eyebrow">{t('review')}</p>
              <h2>{t('reviewBeforeAiDrafting')}</h2>
            </div>
            <InfoIcon className="app-icon section-icon" />
          </div>
          <div className="record-card">
            <strong>{t('draftingPromptSummary')}</strong>
            <p className="helper-text">{activeClient?.name || t('selectClient')}</p>
            <p className="helper-text">{activeCase?.case_number || t('standaloneDraft')}</p>
            <p className="helper-text">{draftForm.instructions || t('draftingInstructionsRequired')}</p>
          </div>
          <div className="record-list">
            {draftSources.length ? draftSources.map((source) => (
              <article key={source.id} className="record-card">
                <strong>{source.name || source.label || t('untitledSource')}</strong>
                <p className="helper-text">
                  {source.status === 'failed'
                    ? t('ocrFailedForSource')
                    : source.extraction_method === 'vision_ocr'
                      ? t('ocrReadSuccess')
                      : t('textReadSuccess')}
                </p>
                {source.error_message ? <p className="inline-feedback inline-feedback--error">{source.error_message}</p> : null}
                <textarea
                  rows="6"
                  value={source.reviewed_text || source.raw_extracted_text || ''}
                  onChange={(event) => handleSourceReviewChange(source.id, event.target.value)}
                  placeholder={source.status === 'failed' ? t('manualContextFallbackPlaceholder') : t('reviewedTextPlaceholder')}
                />
              </article>
            )) : (
              <div className="record-card">
                <strong>{t('noSourceFilesAdded')}</strong>
                <p className="helper-text">{t('draftingContextSubtitle')}</p>
              </div>
            )}
          </div>
          {promptPreviewText ? (
            <div className="record-card">
              <strong>{t('promptPreview')}</strong>
              <textarea value={promptPreviewText} readOnly rows="8" />
            </div>
          ) : null}
          {failedSources.length ? (
            <p className="inline-feedback inline-feedback--error">
              {t('ocrFailureManualFallbackHint')}
            </p>
          ) : null}
          <div className="button-row top-space">
            <button type="button" className="button" onClick={handleGenerateFromReview} disabled={working || aiLocked}>
              {working ? t('generatingDraft') : t('sendToAiGenerateDraft')}
            </button>
          </div>
          {(working || progress.active) ? (
            <div className="record-card">
              <strong>{progress.stageLabel || t('processingDraftingRequest')}</strong>
              <p className="helper-text">{progress.detail || statusMessage || t('processingDraftingRequest')}</p>
              <div className="workflow-defaults">
                <span>{t('draftingProgressStepLabel', { current: progress.currentStep || 1, total: progress.totalSteps || 4 })}</span>
                <span>{t('draftingProgressFilesLabel', { count: progress.uploadedFiles || draftSources.length || selectedFiles.length })}</span>
                <span>{t('draftingProgressEstimatedTokensLabel', { count: progress.estimatedTokens || estimatePromptTokens() })}</span>
              </div>
              {working ? <LoadingState compact label={statusMessage || t('processing')} /> : null}
            </div>
          ) : null}
          {statusMessage ? <p className="inline-feedback">{statusMessage}</p> : null}
        </section>
        {showProcessingOverlay ? (
          <LoadingState overlay label={draftingOverlayLabel}>
            <div className="loading-state__meta">
              <p>{aadhaarStatus.loading ? t('aadhaarProcessingBody') : (progress.detail || statusMessage || t('draftingProcessingBody'))}</p>
              <div className="workflow-defaults">
                <span>{t('draftingProgressStepLabel', { current: progress.currentStep || 1, total: progress.totalSteps || 4 })}</span>
                <span>{t('draftingProgressFilesLabel', { count: progress.uploadedFiles || draftSources.length || selectedFiles.length })}</span>
                <span>{t('draftingProgressEstimatedTokensLabel', { count: progress.estimatedTokens || estimatePromptTokens() })}</span>
              </div>
            </div>
          </LoadingState>
        ) : null}
      </PageShell>
    );
  }

  if (view === 'validate' && currentSession && output) {
    return (
      <PageShell
        title={t('validateDraftFacts')}
        subtitle={currentSession.case_number || activeClient?.name || t('aiDraftingSubtitle')}
        showBack
        backTo="/drafting"
        backReplace
      >
        <section className="panel workflow-card">
          <div className="section-heading">
            <div>
              <p className="eyebrow">{workflowLabels[currentSession.status] || t('review')}</p>
              <h2>{t('validateKeyFacts')}</h2>
            </div>
            <InfoIcon className="app-icon section-icon" />
          </div>
          <p className="helper-text">{t('factValidationHint')}</p>
          {validationFields.length ? (
            <div className="form-grid top-space">
              {validationFields.map((field) => (
                <div key={field.key} className="form-group">
                  <label>{field.label}</label>
                  <input type="text" value={field.value || ''} onChange={(event) => handleValidationChange(field.key, event.target.value)} />
                </div>
              ))}
            </div>
          ) : (
            <div className="record-card top-space">
              <strong>{t('draftingNoValidationFieldsTitle')}</strong>
              <p className="helper-text">{t('draftingNoValidationFieldsBody')}</p>
            </div>
          )}
          <div className="button-row top-space">
            <button type="button" className="button" onClick={handleFactValidation} disabled={working}>{working ? t('generatingDraft') : t('applyFactsAndContinue')}</button>
            <button type="button" className="button button--secondary" onClick={() => setSearchParams((current) => { const next = new URLSearchParams(current); next.set('view', 'review'); return next; }, { replace: true })}>{t('skipToDraftReview')}</button>
          </div>
          {statusMessage ? <p className="inline-feedback">{statusMessage}</p> : null}
        </section>
        {showProcessingOverlay ? (
          <LoadingState overlay label={draftingOverlayLabel}>
            <div className="loading-state__meta">
              <p>{progress.detail || statusMessage || t('draftingProcessingBody')}</p>
            </div>
          </LoadingState>
        ) : null}
      </PageShell>
    );
  }

  if (view === 'review' && output) {
    return (
      <PageShell
        title={t('firstDraft')}
        subtitle={currentSession?.case_number || activeClient?.name || t('aiDraftingSubtitle')}
        showBack
        backTo="/drafting"
        backReplace
        actions={activeCase ? <button type="button" className="icon-button" onClick={() => navigate(`/cases/${activeCase.id}`)}><CasesIcon className="app-icon" /></button> : null}
      >
        <section className="panel draft-review-shell">
          <textarea
            className="draft-review-shell__editor"
            rows="24"
            value={output.edited_text || output.generated_text || ''}
            onChange={(event) => setOutput((current) => ({ ...current, edited_text: event.target.value }))}
          />
          <div className="button-row top-space">
            <button type="button" className="button" onClick={handleSaveDraftEdits} disabled={working}>{t('saveDraft')}</button>
            <button type="button" className="button button--secondary" onClick={handleCopy}><CopyIcon className="app-icon" /><span>{t('copyText')}</span></button>
            <button type="button" className="button button--secondary" onClick={handleExport} disabled={working}>{t('exportDocx')}</button>
            <button type="button" className="button button--secondary" onClick={handlePublish} disabled={working || !canPublishDraft}>{t('publishToCaseDocuments')}</button>
          </div>
          {statusMessage ? <p className="inline-feedback">{statusMessage}</p> : null}
        </section>
        {showProcessingOverlay ? (
          <LoadingState overlay label={draftingOverlayLabel}>
            <div className="loading-state__meta">
              <p>{progress.detail || statusMessage || t('draftingProcessingBody')}</p>
            </div>
          </LoadingState>
        ) : null}
      </PageShell>
    );
  }

  return (
    <PageShell
      title={t('aiDraftingAssistant')}
      subtitle={t('aiDraftingLeanSubtitle')}
      showBack
      actions={currentSession ? <button type="button" className="icon-button icon-button--danger" onClick={() => handleDiscardSession(currentSession.id)} disabled={working}><DeleteIcon className="app-icon" /></button> : null}
    >
      {composeStep === 0 ? (
      <section className="panel panel--accent workflow-card">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{t('startWorkflow')}</p>
            <h2>{t('singleFlowDrafting')}</h2>
          </div>
          <InfoIcon className="app-icon section-icon" />
        </div>
        <p className="helper-text">{t('draftingContextSubtitle')}</p>
        <div className="button-row top-space">
          <button type="button" className="button" onClick={startComposeFlow}>
            {t('start')}
          </button>
        </div>
        {!working && statusMessage ? <p className="inline-feedback">{statusMessage}</p> : null}
      </section>
      ) : null}

      {composeStep === 0 && sessions.length ? (
        <section className="panel workflow-card">
          <div className="section-heading">
            <div><p className="eyebrow">{t('savedWorkflows')}</p><h2>{t('resumeOrDiscard')}</h2></div>
          </div>
          <div className="record-list">
            {sessions.slice(0, 5).map((session) => (
              <article key={session.id} className="record-item">
                <button type="button" className="workflow-session" onClick={() => openSession(session.id)}>
                  <strong>{session.client_name || t('standaloneDraft')}</strong>
                  <p>{session.case_number || t('standaloneDraft')} | {workflowLabels[session.status] || session.status}</p>
                </button>
                <div className="inline-actions">
                  <button type="button" className="icon-button" onClick={() => openSession(session.id)}><ArrowRightIcon className="app-icon" /></button>
                  <button type="button" className="icon-button icon-button--danger" onClick={() => handleDiscardSession(session.id)} disabled={working}><DeleteIcon className="app-icon" /></button>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {composeStep === 1 ? (
      <section className="panel workflow-card">
        <div className="section-heading">
          <div><p className="eyebrow">{t('stepOne')}</p><h2>{t('chooseClientAndCase')}</h2></div>
          <button
            type="button"
            className="icon-button icon-button--accent"
            onClick={() => {
              setShowNewClientForm((current) => !current);
              setNewClientIntakeMode('aadhaar');
              setAadhaarStatus(emptyAadhaarStatus);
            }}
          >
            <PlusIcon className="app-icon" />
          </button>
        </div>
        {!showNewClientForm ? (
          <>
            <div className="form-grid">
              <div className="form-group">
                <label>{t('clientLabel')}</label>
                <select
                  value={draftForm.clientId}
                  onChange={(event) => setDraftForm((current) => ({ ...current, clientId: event.target.value, caseId: '' }))}
                >
                  <option value="">{t('selectClient')}</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name} {isClientDraftReady(client) ? '' : `(${t('draftProfileIncomplete')})`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('linkedMatter')}</label>
                <select
                  value={draftForm.caseId}
                  onChange={(event) => {
                    const selectedCase = cases.find((caseRecord) => caseRecord.id === event.target.value) || null;
                    setDraftForm((current) => ({
                      ...current,
                      caseId: event.target.value,
                      clientId: findClientIdForCaseRecord(selectedCase, clients) || current.clientId,
                    }));
                  }}
                >
                  <option value="">{t('standaloneDraft')}</option>
                  {availableCases.map((caseRecord) => (
                    <option key={caseRecord.id} value={caseRecord.id}>{caseRecord.case_number} - {caseRecord.client_name}</option>
                  ))}
                </select>
              </div>
            </div>
            {activeClient && !isClientDraftReady(activeClient) ? (
              <p className="inline-feedback inline-feedback--error">
                {t('clientProfileIncompleteForDrafting')} <button type="button" className="text-link text-link--button" onClick={() => navigate(`/clients/${activeClient.id}?edit=1`)}>{t('openClientProfile')}</button>
              </p>
            ) : null}
            <div className="button-row top-space">
              <button
                type="button"
                className="button"
                disabled={!draftForm.clientId || !isActiveClientDraftReady}
                onClick={() => moveToComposeStep(2)}
              >
                {t('continueLabel')}
              </button>
            </div>
          </>
        ) : null}
        {showNewClientForm ? (
          <form onSubmit={handleCreateClient} className="top-space">
            <div className="workflow-section-stack">
              <p className="helper-text">{t('aadhaarIntakeChoiceSubtitle')}</p>
              <div className="workflow-choice-row">
                <button
                  type="button"
                  className={`workflow-choice${newClientIntakeMode === 'aadhaar' ? ' workflow-choice--selected' : ''}`}
                  onClick={() => setNewClientIntakeMode('aadhaar')}
                >
                  <strong>{t('useAadhaarFlow')}</strong>
                  <p>{t('aadhaarFlowHint')}</p>
                </button>
                <button
                  type="button"
                  className={`workflow-choice${newClientIntakeMode === 'manual' ? ' workflow-choice--selected' : ''}`}
                  onClick={() => setNewClientIntakeMode('manual')}
                >
                  <strong>{t('useManualFlow')}</strong>
                  <p>{t('manualFlowHint')}</p>
                </button>
              </div>

              {newClientIntakeMode === 'aadhaar' && !shouldShowNewClientFields ? (
                <div className="workflow-helper-card">
                  <strong>{t('aadhaarUploadPreferred')}</strong>
                  <input type="file" accept="image/*,application/pdf" onChange={(event) => handleAadhaarUpload(event.target.files?.[0] || null)} />
                  <p>{t('aadhaarFlowHint')}</p>
                  {aadhaarStatus.error ? <p className="inline-feedback inline-feedback--error">{aadhaarStatus.error}</p> : null}
                </div>
              ) : null}

              {shouldShowNewClientFields ? (
                <div className="form-grid">
                  {newClientIntakeMode === 'aadhaar' ? (
                    <div className="form-group full-span"><label>{t('aadhaarUploadPreferred')}</label><input type="file" accept="image/*,application/pdf" onChange={(event) => handleAadhaarUpload(event.target.files?.[0] || null)} /></div>
                  ) : null}
                  {aadhaarStatus.success ? <p className="inline-feedback full-span">{t('aadhaarReadSuccess')}</p> : null}
                  {aadhaarStatus.error ? <p className="inline-feedback inline-feedback--error full-span">{aadhaarStatus.error}</p> : null}
                  {aadhaarStatus.warnings.length ? (
                    <div className="record-card full-span">
                      <strong>{t('aadhaarNeedsReview')}</strong>
                      {aadhaarStatus.warnings.map((warning) => <p key={warning} className="helper-text">{warning}</p>)}
                    </div>
                  ) : null}
                  <div className="form-group"><label>{t('name')}</label><input type="text" value={clientForm.name} onChange={(event) => setClientForm((current) => ({ ...current, name: event.target.value }))} required /></div>
                  <div className="form-group"><label>{t('phone')}</label><input type="text" value={clientForm.phone} onChange={(event) => setClientForm((current) => ({ ...current, phone: event.target.value }))} required /></div>
                  <div className="form-group"><label>{t('email')}</label><input type="email" value={clientForm.email} onChange={(event) => setClientForm((current) => ({ ...current, email: event.target.value }))} /></div>
                  <div className="form-group"><label>{t('preferredLanguage')}</label><select value={clientForm.preferredLanguage} onChange={(event) => setClientForm((current) => ({ ...current, preferredLanguage: event.target.value }))}><option value="en">{t('english')}</option><option value="hi">{t('hindi')}</option></select></div>
                  <div className="form-group"><label>{t('relationLabel')}</label><select value={clientForm.relationLabel} onChange={(event) => setClientForm((current) => ({ ...current, relationLabel: event.target.value }))}>{relationLabelOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select></div>
                  <div className="form-group"><label>{t('relationName')}</label><input type="text" value={clientForm.relationName} onChange={(event) => setClientForm((current) => ({ ...current, relationName: event.target.value }))} required /></div>
                  <div className="form-group"><label>{t('age')}</label><input type="number" value={clientForm.age} onChange={(event) => setClientForm((current) => ({ ...current, age: event.target.value }))} required /></div>
                  <div className="form-group"><label>{t('dateOfBirth')}</label><input type="date" value={clientForm.dateOfBirth} onChange={(event) => setClientForm((current) => ({ ...current, dateOfBirth: event.target.value, age: current.age || calculateAgeFromDateOfBirth(event.target.value) }))} required /></div>
                  <div className="form-group"><label>{t('gender')}</label><select value={clientForm.gender} onChange={(event) => setClientForm((current) => ({ ...current, gender: event.target.value }))}>{genderOptions.map((option) => <option key={option} value={option}>{t(option.toLowerCase())}</option>)}</select></div>
                  <div className="form-group full-span"><label>{t('address')}</label><textarea value={clientForm.address} onChange={(event) => setClientForm((current) => ({ ...current, address: event.target.value }))} required /></div>
                  <div className="form-group"><label>{t('aadhaarName')}</label><input type="text" value={clientForm.aadhaarName} onChange={(event) => setClientForm((current) => ({ ...current, aadhaarName: event.target.value }))} required /></div>
                  <div className="form-group"><label>{t('aadhaarNumber')}</label><input type="text" value={clientForm.aadhaarNumber} onChange={(event) => setClientForm((current) => ({ ...current, aadhaarNumber: event.target.value }))} required /></div>
                  {aadhaarStatus.rawText ? <div className="form-group full-span"><label>{t('aadhaarOcrPreview')}</label><textarea value={aadhaarStatus.rawText} readOnly rows="5" /></div> : null}
                </div>
              ) : null}
            </div>
            {shouldShowNewClientFields ? (
              <button type="submit" className="button" disabled={working}>{t('addClientAndContinue')}</button>
            ) : null}
          </form>
        ) : null}
      </section>
      ) : null}

      {composeStep === 2 ? (
      <section className="panel workflow-card">
        <div className="section-heading">
          <div><p className="eyebrow">{t('stepTwo')}</p><h2>{t('uploadSourceDocumentsOptional')}</h2></div>
          <DocumentsIcon className="app-icon section-icon" />
        </div>
        <div
          className={`dropzone${working ? ' dropzone--disabled' : ''}`}
          {...getRootProps({
            onClick: aiLocked
              ? (event) => {
                  event.preventDefault();
                  requestPremiumFeature();
                }
              : undefined,
          })}
        >
          <input {...getInputProps()} />
          <p>{t('draftingDropzoneTitle')}</p>
          <small>{t('optionalSourceUploadHint')}</small>
        </div>
        {selectedFiles.length ? (
          <div className="workflow-defaults top-space">
            {selectedFiles.map((file) => <span key={`${file.name}-${file.size}`}>{file.name}</span>)}
          </div>
        ) : null}
        <div className="button-row top-space">
          <button type="button" className="button button--secondary" onClick={() => moveToComposeStep(1)}>
            {t('back')}
          </button>
          <button type="button" className="button" onClick={() => moveToComposeStep(3)}>
            {t('continueLabel')}
          </button>
        </div>
      </section>
      ) : null}

      {composeStep === 3 ? (
      <section className="panel workflow-card">
        <div className="section-heading">
          <div><p className="eyebrow">{t('stepThree')}</p><h2>{t('advocateInstructions')}</h2></div>
        </div>
        <div className="form-group">
          <label>{t('draftInstructions')}</label>
          <textarea
            rows="6"
            value={draftForm.instructions}
            onChange={(event) => setDraftForm((current) => ({ ...current, instructions: event.target.value }))}
            placeholder={t('draftInstructionsPlaceholder')}
          />
        </div>
        <div className="button-row top-space">
          <button type="button" className="button button--secondary" onClick={() => moveToComposeStep(2)}>
            {t('back')}
          </button>
          <button type="button" className="button" onClick={startDrafting} disabled={working}>{working ? t('generatingDraft') : t('generateDraft')}</button>
        </div>
        {usageSummaryNote ? (
          <div className="workflow-defaults top-space">
            <span>{usageSummaryNote}</span>
          </div>
        ) : null}
        {(working || progress.active) ? (
          <div className="record-card top-space">
            <strong>{progress.stageLabel || t('processingDraftingRequest')}</strong>
            <p className="helper-text">{progress.detail || statusMessage || t('processingDraftingRequest')}</p>
            <div className="workflow-defaults">
              <span>{t('draftingProgressStepLabel', { current: progress.currentStep || 1, total: progress.totalSteps || 4 })}</span>
              <span>{t('draftingProgressFilesLabel', { count: progress.uploadedFiles || selectedFiles.length })}</span>
              <span>{t('draftingProgressEstimatedTokensLabel', { count: progress.estimatedTokens || estimatePromptTokens() })}</span>
            </div>
            {working ? <LoadingState compact label={statusMessage || t('processing')} /> : null}
          </div>
        ) : null}
      </section>
      ) : null}

      {output ? (
        <section className="panel workflow-card">
          <div className="section-heading">
            <div><p className="eyebrow">{t('firstDraft')}</p><h2>{t('draftPreview')}</h2></div>
          </div>
          <div className="draft-preview">
            <strong>{activeClient?.name || t('generatedDraft')}</strong>
            <p>{(output.edited_text || output.generated_text || '').slice(0, 320)}...</p>
            <div className="button-row">
              <button type="button" className="button" onClick={() => setSearchParams((current) => { const next = new URLSearchParams(current); next.set('view', output.fact_validation_required ? 'validate' : 'review'); return next; }, { replace: true })}>
                {output.fact_validation_required ? t('validateKeyFacts') : t('openFullScreenReview')}
              </button>
            </div>
          </div>
        </section>
      ) : null}
      {showProcessingOverlay ? (
        <LoadingState overlay label={draftingOverlayLabel}>
          <div className="loading-state__meta">
            <p>{aadhaarStatus.loading ? t('aadhaarProcessingBody') : (progress.detail || statusMessage || t('draftingProcessingBody'))}</p>
            <div className="workflow-defaults">
              <span>{t('draftingProgressStepLabel', { current: progress.currentStep || 1, total: progress.totalSteps || 4 })}</span>
              <span>{t('draftingProgressFilesLabel', { count: progress.uploadedFiles || draftSources.length || selectedFiles.length })}</span>
              <span>{t('draftingProgressEstimatedTokensLabel', { count: progress.estimatedTokens || estimatePromptTokens() })}</span>
            </div>
          </div>
        </LoadingState>
      ) : null}
      {showPremiumModal ? (
        <div className="app-modal">
          <button type="button" className="app-modal__scrim" aria-label={t('closeNavigation')} onClick={() => setShowPremiumModal(false)} />
          <div className="app-modal__surface">
            <p className="eyebrow">{t('premiumAccessEyebrow')}</p>
            <h2>{t('draftingPremiumGateTitle')}</h2>
            <p>{t('draftingPremiumGateBody')}</p>
            <div className="workflow-defaults">
              <span>{aiSummary ? getAiCreditHeadline(aiSummary) : t('noAiCreditsLeft')}</span>
              {usageEstimate ? (
                <span>
                  {t('draftingEstimatedCredits', {
                    credits: usageEstimate.estimatedCredits || 0,
                    input: usageEstimate.inputTokens || 0,
                    output: usageEstimate.outputTokens || 0,
                    ocr: usageEstimate.ocrUnits || 0,
                  })}
                </span>
              ) : null}
            </div>
            <div className="button-row top-space">
              <button type="button" className="button" onClick={() => navigate('/premium?feature=drafting')}>
                {t('managePremiumAccess')}
              </button>
              <button type="button" className="button button--secondary" onClick={() => setShowPremiumModal(false)}>
                {t('back')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </PageShell>
  );
};

export default DraftingAssistant;
